import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Third Party
import { v4 as uuidv4 } from "uuid";

// Access
import ACTIONS from "@/Constants/actionsList";

// API's
import { createPayPlanAssignmentGroupPayPlan } from "@/PayPlans/api/assignments/mutations";
import { getUsersByPositionGroupPayPlanAssignment } from "@/PayPlans/api/users/queries";
import { retrieveSalesPp } from "@/PayPlans/api/sales/associates/queries";

// Hooks
import { useManagedPositions } from "@/Common/hooks/Positions";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Layouts
import AssignPayPlanButton from "@/Layouts/components/AssignPayPlanButton";
import SearchInput from "@/Layouts/components/Basics/SearchInput";

// Custom components
import SelectedItemContent from "./SelectedItemContent";
import GroupDialog from "@PayPlans/components/Assignments/GroupDialog";

// MUI
import { Checkbox, Divider, FormControlLabel, Grid, Typography } from "@mui/material";

// Icons
import AssignmentCardGroup from "@PayPlans/components/Assignments/Contents/Group/AssignmentCardGroup";
import { and } from "@Common/helpers/sonar";
import { getSystemSettingsWashoutReviewers } from "@/Washouts/api/common/queries";
import { getSupervisorsByPositionHierarchy } from "@/PayPlans/api/assignments/queries";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import useCanAssignPayPlan from "../../hooks/useCanAssignPayPlan";

export default function Group(props) {
	const { selectedItem = null } = props;

	const queryClient = useQueryClient();
	const user = useSelector((state) => state.user.user);
	const location = useSelector((state) => state.location.location);
	const locationId = location?.locationId;

	// Local state
	const [openPayPlanModel, setOpenPayPlanModel] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const [selectedList, setSelectedList] = useState([]);
	const [payPlanSelected, setPayPlanSelected] = useState(null);
	const [selectedSupervisor, setSelectedSupervisor] = useState("");

	const { matchPosition, isLoading: isLoadingPositions } = useManagedPositions({
		queryOpts: {
			enabled: !!selectedItem?.position,
		},
	});

	const position = matchPosition(selectedItem?.position);

	const selectedTitle = selectedItem?.title;
	const selectedPosition = selectedItem?.position;

	const { getSubscription } = useSubscriptions();
	const groupPayPlanAssignmentSubscription = getSubscription([
		"groupPayPlanAssignmentUsersByPosition",
		locationId,
		selectedPosition,
		selectedTitle,
	]);

	groupPayPlanAssignmentSubscription?.setInvalidate([
		"groupPayPlanAssignmentUsersByPosition",
		locationId,
		selectedPosition,
		selectedTitle,
	]);
	groupPayPlanAssignmentSubscription?.subscriptions?.onCreatePayPlanAssignment?.setCallback((res) => {
		if (res) {
			const newUsers = res.data.onCreatePayPlanAssignment?.users;
			queryClient.setQueryData(["groupPayPlanAssignmentUsersByPosition", locationId, selectedPosition, selectedTitle], (oldUsers) => {
				return oldUsers.map((oldUser) => {
					const isDiff = newUsers?.find((newUser) => newUser._id === oldUser._id);
					return isDiff ? { ...oldUser, ...isDiff } : oldUser;
				});
			});
		}
	});

	/********************************************* QUERIES ************************************************/
	const { data: usersData, isLoading: isLoadingUsers } = useQuery({
		queryKey: ["groupPayPlanAssignmentUsersByPosition", locationId, selectedPosition, selectedTitle],
		queryFn: async () => getUsersByPositionGroupPayPlanAssignment(locationId, position, "ACTIVE"),
		staleTime: Infinity,
		enabled: and(!!locationId, !!position?.length, !!selectedItem),
	});

	const { data: positionSupervisors, isLoading: isLoadingDepartmentsHierarchy } = useQuery({
		queryKey: ["getSupervisorsByPositionHierarchy", selectedPosition, locationId],
		queryFn: async () => getSupervisorsByPositionHierarchy(locationId, selectedPosition),
		enabled: and(!!locationId, !!selectedPosition),
		staleTime: Infinity,
	});

	const selectDefaultSupervisor = (reviewers) => {
		if (position?.length > 0) {
			for (const pos of position) {
				const foundReviewer = reviewers?.washoutReviewers?.find((rev) => rev.position === pos);
				if (foundReviewer) {
					setSelectedSupervisor(foundReviewer.reviewerId ?? null);
					return true;
				}
			}
		} else if (position?.length === 0) {
			const foundReviewer = reviewers?.washoutReviewers?.find((rev) => rev.position === position?.[0]);
			setSelectedSupervisor(foundReviewer?.reviewerId ?? null);
			return true;
		}
		setSelectedSupervisor(null);
		return false;
	};

	const { data: reviewersData } = useQuery({
		queryKey: ["getSystemSettingsWashoutReviewers", locationId],
		queryFn: async () => {
			const resp = await getSystemSettingsWashoutReviewers(locationId ?? "");
			selectDefaultSupervisor(resp);
			return resp;
		},
		enabled: !!locationId,
		staleTime: Infinity,
	});

	/********************************************* MUTATIONS ************************************************/
	const { mutate, isPending } = useMutation({
		mutationFn: (input) => createPayPlanAssignmentGroupPayPlan({ locationId, input, userIds: selectedList, planId: selectedItem._id }),
		onSuccess: (res) => {
			const newUsers = res.data.data?.createPayPlanAssignment?.users;
			queryClient.setQueryData(["groupPayPlanAssignmentUsersByPosition", locationId, selectedPosition, selectedTitle], (oldUsers) => {
				return oldUsers.map((oldUser) => {
					const isDiff = newUsers?.find((newUser) => newUser._id === oldUser._id);
					return isDiff ? { ...oldUser, ...isDiff } : oldUser;
				});
			});
			setSelectedList([]);
		},
	});

	const hasPermission = useCanAssignPayPlan(selectedPosition);

	const filteredData = usersData?.filter((userInfo) => {
		const list = userInfo?.currentPayPlanAssignment?.planId?.name !== selectedItem.title;
		if (searchValue) {
			const name = userInfo.fullName.toLowerCase();
			return name.startsWith(searchValue);
		}
		return list;
	});

	const positions = filteredData?.map((item) => item.position);
	const filteredDataPosition = [...new Set(positions)].reduce((acc, localPosition) => {
		acc[localPosition] = filteredData.filter((data) => data.position === localPosition);
		return acc;
	}, {});

	const onChangeSearchHandler = (e) => {
		setSearchValue(e.toLowerCase());
	};
	const onClearSearchHandler = () => {
		setSearchValue("");
	};

	const handleOpenPayPlanModel = (id) => {
		setOpenPayPlanModel(true);
		setPayPlanSelected(id);
		retrieveSalesPp(user.locationId, id).catch((err) => console.log(err));
	};

	const selectItemHandler = (id, e) => {
		if (e.target.checked) {
			setSelectedList((oldValue) => [...oldValue, id]);
		} else {
			setSelectedList((oldValue) => oldValue.filter((item) => item !== id));
		}
	};

	const payPlanAssignmentHandler = async (date) => {
		const today = new Date();
		const input = {
			_id: uuidv4(),
			position: position[0],
			planId: selectedItem._id,
			assignmentTypeGroup: true,
			assignmentDate: today,
			assignedBy: user._id,
			effectiveDate: date,
			acknowledgementDate: null,
			acknowledgementDocumentLink: "",
			supervisorId: selectedSupervisor?._id ?? null,
		};
		try {
			mutate(input);
		} catch (err) {
			console.log(err);
		}
	};

	useEffect(() => {
		setSelectedList([]);
		return () => {
			setSelectedList([]);
			setPayPlanSelected(null);
		};
	}, [selectedItem]);

	useEffect(() => {
		if (!selectedItem.position || !locationId) {
			return;
		}
		setSelectedList([]);
		selectDefaultSupervisor(reviewersData);
	}, [locationId, selectedItem?.position]);

	const isGlobalLoading = isLoadingUsers || isPending;

	return (
		<SelectedItemContent
			selected={selectedItem}
			filteredData={filteredData}
			searchValue={searchValue}
			loading={isGlobalLoading || isLoadingPositions}
		>
			<>
				<GroupDialog
					payPlanSelected={payPlanSelected}
					open={openPayPlanModel}
					onCloseAction={() => {
						setOpenPayPlanModel(false);
						setPayPlanSelected(null);
					}}
				/>

				<Grid container direction="column">
					<Grid item>
						<Typography className="group-pay-plan-assign-container-title">{props.selectedItem.position}</Typography>
					</Grid>
					<Grid item sx={{ marginTop: "20px" }}>
						<Grid container direction="row" justifyContent="space-between">
							<Grid item xs={3}>
								<SearchInput
									disabled={(filteredData?.length === 0 && !searchValue) || isLoadingUsers}
									value={searchValue}
									onChange={onChangeSearchHandler}
									clearSearch={onClearSearchHandler}
								/>
							</Grid>
							<Grid item justifyContent="flex-end" alignItems="flex-end" sx={{ width: "160px", height: "100%" }}>
								<AssignPayPlanButton
									disabled={
										!hasPermission ||
										isGlobalLoading ||
										filteredData?.length === 0 ||
										selectedList?.length === 0
									}
									onDateChanged={(date) => payPlanAssignmentHandler(date)}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid container className="!mt-[32px]">
						<Grid item className="!mr-[8px]">
							<Typography className="select-plan-dialog-employee-data-title !font-medium">Supervisor:</Typography>
						</Grid>
						<Grid item>
							<SmartSelector
								loading={isLoadingDepartmentsHierarchy}
								tagsVariant="text"
								classes="!w-[218px] !mt-[-4px]"
								showBy={"fullName"}
								selectBy={"_id"}
								selected={selectedSupervisor}
								options={positionSupervisors || []}
								inputProps={{
									placeholder: "Supervisor",
								}}
								listBoxProps={{
									sx: { maxHeight: "223px" },
								}}
								popperProps={{
									sx: { minWidth: "190px", maxWidth: "240px", zIndex: "9999 !important" },
								}}
								error={false}
								onChangeAction={(sel) => {
									setSelectedSupervisor(sel);
								}}
							/>
						</Grid>
					</Grid>
					<Grid container className="!mt-[16px] !mb-[12px]">
						<Grid item className="!mr-[8px]">
							<Typography className="select-plan-dialog-employee-data-title !font-medium">Corporate Sign Off:</Typography>
						</Grid>
						<Grid item>
							<SmartSelector
								loading={isLoadingDepartmentsHierarchy}
								tagsVariant="text"
								classes="!w-[218px] !mt-[-4px]"
								showBy={"fullName"}
								selectBy={"_id"}
								options={[]}
								inputProps={{
									placeholder: "Optional",
								}}
								listBoxProps={{
									sx: { maxHeight: "223px" },
								}}
								popperProps={{
									sx: { minWidth: "190px", maxWidth: "240px", zIndex: "9999 !important" },
								}}
								error={false}
								onChangeAction={(sel) => {
									setSelectedSupervisor(sel);
								}}
							/>
						</Grid>
					</Grid>

					<Grid item sx={{ paddingLeft: "0px" }}>
						{Object.entries(filteredDataPosition).map(([pos, data], index) => {
							return (
								<Grid key={pos}>
									<AssignmentCardGroup
										key={pos}
										data={data}
										position={pos}
										isPending={isPending}
										selectedList={selectedList}
										selectItemHandler={selectItemHandler}
										onOpenPayPlanModel={handleOpenPayPlanModel}
									/>
									{index < Object.entries(filteredDataPosition).length - 1 && <Divider className={"!mt-8 !mb-4"} />}
								</Grid>
							);
						})}
					</Grid>
				</Grid>
			</>
		</SelectedItemContent>
	);
}
Group.propTypes = {
	selectedItem: PropTypes.object,
};
